/**
 * useMapSearch hook.
 *
 * Provides debounced search with keyboard navigation, stale-request
 * cancellation, and combined external + internal results.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { MapSearchResult, GeoCoord } from '../types/mapTypes';
import { retrievePlace, searchMap, suggestPlaces, type SuggestResult } from '../api/mapApi';
import { SEARCH_DEBOUNCE_MS } from '../config/mapboxConfig';

interface UseMapSearchOptions {
  debounceMs?: number;
  proximity?: GeoCoord;
  limit?: number;
  onResultSelect?: (result: MapSearchResult) => void;
}

interface UseMapSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: MapSearchResult[];
  suggestions: SuggestResult[];
  loading: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (i: number) => void;
  selectResult: (result: MapSearchResult | SuggestResult) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  clearSearch: () => void;
}

export function useMapSearch(options: UseMapSearchOptions = {}): UseMapSearchReturn {
  const {
    debounceMs = SEARCH_DEBOUNCE_MS,
    proximity,
    limit = 10,
    onResultSelect,
  } = options;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MapSearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef(crypto.randomUUID());

  // Combined results for keyboard navigation
  const allItems = [...suggestions, ...results];

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    sessionTokenRef.current = crypto.randomUUID();
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.length < 2) {
      setResults([]);
      setSuggestions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setIsOpen(true);

    debounceRef.current = setTimeout(async () => {
      // Cancel any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const proximityStr = proximity ? `${proximity[0]},${proximity[1]}` : undefined;

        // Fetch suggestions and full results in parallel
        const [suggestResults, searchResults] = await Promise.allSettled([
          suggestPlaces({
            q: query,
            sessionToken: sessionTokenRef.current,
            proximity: proximityStr,
            limit,
          }),
          searchMap({
            q: query,
            proximity: proximityStr,
            limit,
          }),
        ]);

        if (controller.signal.aborted) return;

        setSuggestions(
          suggestResults.status === 'fulfilled' ? suggestResults.value : [],
        );
        setResults(
          searchResults.status === 'fulfilled' ? searchResults.value : [],
        );
      } catch {
        if (controller.signal.aborted) return;
        setResults([]);
        setSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, proximity, limit, debounceMs]);

  const selectResult = useCallback(
    async (item: MapSearchResult | SuggestResult) => {
      if ('source' in item && 'entityType' in item) {
        // MapSearchResult
        onResultSelect?.(item as MapSearchResult);
      } else {
        const suggest = item as SuggestResult;
        try {
          const retrieved = await retrievePlace(suggest.id, sessionTokenRef.current);
          onResultSelect?.(retrieved);
        } catch {
          // Keep the search open so the user can retry another result.
          setIsOpen(true);
          return;
        }
      }
      setQuery(item.label);
      setIsOpen(false);
      setSelectedIndex(-1);
      sessionTokenRef.current = crypto.randomUUID();
    },
    [onResultSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || allItems.length === 0) {
        if (e.key === 'Escape') clearSearch();
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % allItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev <= 0 ? allItems.length - 1 : prev - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < allItems.length) {
            selectResult(allItems[selectedIndex]);
          }
          break;
        case 'Escape':
          clearSearch();
          break;
      }
    },
    [isOpen, allItems, selectedIndex, selectResult, clearSearch],
  );

  return {
    query,
    setQuery,
    results,
    suggestions,
    loading,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    selectResult,
    handleKeyDown,
    clearSearch,
  };
}
